import React, { useState, useEffect, useCallback } from 'react';
import API_ENDPOINTS from '../config/api';

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString();
};

const summarizeCounts = (manifest) => {
  const counts = manifest && manifest.counts;
  if (!counts) return '';
  const parts = [];
  if (typeof counts.objectCount === 'number') parts.push(`${counts.objectCount} objects`);
  if (typeof counts.fieldCount === 'number') parts.push(`${counts.fieldCount} fields`);
  if (typeof counts.flowCount === 'number') parts.push(`${counts.flowCount} flows`);
  return parts.join(' · ');
};

const ContextPackPanel = ({ getAuthHeaders }) => {
  const [includeObjects, setIncludeObjects] = useState(true);
  const [includeFlows, setIncludeFlows] = useState(true);
  const [customOnly, setCustomOnly] = useState(false);
  const [packs, setPacks] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  const fetchPacks = useCallback(async () => {
    try {
      const response = await fetch(API_ENDPOINTS.DOCUMENTATION_PACKS, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Failed to load context packs (${response.status})`);
      }
      const data = await response.json();
      setPacks(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load context packs');
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchPacks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const response = await fetch(API_ENDPOINTS.DOCUMENTATION_PACK_GENERATE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ includeObjects, includeFlows, customOnly }),
      });
      if (!response.ok) {
        throw new Error(`Failed to generate context pack (${response.status})`);
      }
      await fetchPacks();
    } catch (err) {
      setError(err.message || 'Failed to generate context pack');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (e, id) => {
    e.preventDefault();
    setError(null);
    try {
      const response = await fetch(API_ENDPOINTS.DOCUMENTATION_PACK_DOWNLOAD(id), {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Failed to download context pack (${response.status})`);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `context-pack-${id}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Failed to download context pack');
    }
  };

  return (
    <div className="pack-panel">
      <p className="pack-explainer">
        A Context Pack is your org's metadata as markdown — drop it in a repo and point your AI
        agent at pack/README.md. No record data, ever.
      </p>

      <div className="pack-scope glass-card">
        <div className="pack-scope-controls">
          <label htmlFor="pack-scope-objects" className="pack-checkbox-label">
            <input
              id="pack-scope-objects"
              type="checkbox"
              checked={includeObjects}
              onChange={(e) => setIncludeObjects(e.target.checked)}
            />
            Objects
          </label>
          <label htmlFor="pack-scope-flows" className="pack-checkbox-label">
            <input
              id="pack-scope-flows"
              type="checkbox"
              checked={includeFlows}
              onChange={(e) => setIncludeFlows(e.target.checked)}
            />
            Flows
          </label>
          <label htmlFor="pack-scope-custom-only" className="pack-checkbox-label">
            <input
              id="pack-scope-custom-only"
              type="checkbox"
              checked={customOnly}
              onChange={(e) => setCustomOnly(e.target.checked)}
            />
            Custom Only
          </label>
        </div>
        <button
          className="pack-generate-btn"
          onClick={handleGenerate}
          disabled={generating}
        >
          {generating ? 'Generating…' : 'Generate Context Pack'}
        </button>
      </div>

      {error && <div className="pack-error-banner">{error}</div>}

      <div className="pack-history">
        {packs.length === 0 && (
          <div className="pack-history-empty">No context packs generated yet.</div>
        )}
        {packs.map((pack) => (
          <div key={pack.id} className="pack-history-row glass-card">
            <div className="pack-history-main">
              <div className="pack-history-date">{formatDate(pack.created_at)}</div>
              <div className="pack-history-summary">{summarizeCounts(pack.manifest)}</div>
              <div className="pack-history-files">{pack.file_count} files</div>
            </div>
            <div className="pack-history-actions">
              <span className="pack-token-pill">
                {(pack.token_estimate || 0).toLocaleString()} tokens
              </span>
              <a
                href={API_ENDPOINTS.DOCUMENTATION_PACK_DOWNLOAD(pack.id)}
                className="pack-download-link"
                onClick={(e) => handleDownload(e, pack.id)}
              >
                Download
              </a>
            </div>
          </div>
        ))}
      </div>

      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .pack-panel {
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .pack-explainer {
    color: var(--gray-400);
    font-size: 0.9rem;
    max-width: 60ch;
  }

  .pack-scope {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 1rem;
    padding: 1rem 1.25rem;
  }

  .pack-scope-controls {
    display: flex;
    gap: 1.5rem;
    flex-wrap: wrap;
  }

  .pack-checkbox-label {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    color: var(--gray-400);
    font-size: 0.9rem;
    cursor: pointer;
  }

  .pack-generate-btn {
    background: var(--force-cyan);
    color: #06131a;
    border: none;
    border-radius: 6px;
    padding: 0.55rem 1.1rem;
    font-weight: 600;
    cursor: pointer;
  }

  .pack-generate-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .pack-error-banner {
    color: #ff6b6b;
    font-size: 0.85rem;
  }

  .pack-history {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
  }

  .pack-history-empty {
    color: var(--gray-400);
    font-size: 0.85rem;
  }

  .pack-history-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.75rem;
    padding: 0.85rem 1.1rem;
  }

  .pack-history-main {
    display: flex;
    gap: 1rem;
    align-items: baseline;
    flex-wrap: wrap;
  }

  .pack-history-date {
    color: var(--gray-400);
    font-size: 0.85rem;
  }

  .pack-history-summary,
  .pack-history-files {
    font-family: monospace;
    font-size: 0.85rem;
    color: var(--gray-400);
  }

  .pack-history-actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .pack-token-pill {
    font-family: monospace;
    font-size: 0.8rem;
    background: rgba(0, 229, 255, 0.1);
    color: var(--force-cyan);
    border-radius: 999px;
    padding: 0.25rem 0.65rem;
  }

  .pack-download-link {
    color: var(--force-cyan);
    text-decoration: none;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
  }

  .pack-download-link:hover {
    text-decoration: underline;
  }
`;

export default ContextPackPanel;
